import cn from 'classnames'
import { observer } from 'mobx-react'
import React, { ReactElement, useState, useEffect } from 'react'

import { ClientConfigType } from '@flowwai-frontend/bps-officer-web/iam'
import { useAppTheme } from '@flowwai-frontend/bps-officer-web/style-guide'
import { setCookie, getCookie } from '@flowwai-frontend/shared/utils/web'
import {
   Button,
   Hierarchy,
   SubVariant,
   TextField
} from '@flowwai-frontend/design-system'
import {
   ArrowNarrowIcon,
   MailIcon
} from '@flowwai-frontend/design-system/icons'
import { ALPHA_NUMERIC_UNDERSCORE_REGEX } from '@flowwai-frontend/shared-web/common/ui'
import { useCustomT } from '@flowwai-frontend/shared/common-components'

import {
   LOGIN_USERNAME_COOKIE_EXPIRY_DAYS,
   LOGIN_USERNAME_COOKIE_NAME,
   loginMachineEvents,
   loginMachineTags
} from '../../constants/loginConstants'
import {
   LoginMachineSend,
   LoginMachineState
} from '../../machines/LoginMachine'

import './index.css'
import * as Styles from './styles'

interface LoginWithUsernameProps {
   loginMachineSend: LoginMachineSend
   loginMachineState: LoginMachineState
   clientConfig: ClientConfigType
}

const LoginWithUsername = (props: LoginWithUsernameProps): ReactElement => {
   const { loginMachineSend, loginMachineState, clientConfig } = props

   const {
      context,
      context: { username: defaultUsername = '' }
   } = loginMachineState

   const [username, setUsername] = useState<string>(defaultUsername)
   const [isValid, setIsValid] = useState(defaultUsername ? true : false)
   const [errorMessage, setErrorMessage] = useState<string>('')

   useEffect(() => {
      const savedUsername = getCookie(LOGIN_USERNAME_COOKIE_NAME)
      if (savedUsername) {
         setUsername(savedUsername)
         validate(savedUsername)
      }
   }, [])

   const customT = useCustomT('auth:login')
   const authErrorT = useCustomT('auth:errorMessages')

   const { theme } = useAppTheme()
   const { loginWithEmail: loginCardTheme } = theme.auth.login

   const errorTransitionClassName = 'login-textfield-error-transition '

   const isDefaultLoginLayout = clientConfig.loginLayout.type === 'DEFAULT'

   useEffect(() => {
      if (username) {
         validate(username)
      }
   }, [username])

   const onBlurUsername = (): void => {
      validate(username || '')
   }

   const unsetError = (): void => {
      setIsValid(true)
      setError('')
   }

   const setError = (errorText: string): void => {
      setErrorMessage(errorText)
   }

   const validateUsername = (value: string) => {
      const isValid = ALPHA_NUMERIC_UNDERSCORE_REGEX.test(value)
      return isValid
   }

   const validate = (value: string): void => {
      if (!value) {
         setError(authErrorT('required'))
         setIsValid(false)
         return
      }
      const isValidUsername = validateUsername(value)

      if (isValidUsername) {
         unsetError()
         setIsValid(true)
      } else {
         setError(authErrorT('invalidUsername'))
         setIsValid(false)
      }
   }

   const onChangeUsername = (value: string): void => {
      setUsername(value)

      validate(value)
      loginMachineSend({
         type: loginMachineEvents.onChangeValue
      })
   }

   const onClickSendOTP = (event: React.SyntheticEvent): void => {
      event.preventDefault()
      if (isValid) {
         if (!username) return
         setCookie(
            LOGIN_USERNAME_COOKIE_NAME,
            username,
            LOGIN_USERNAME_COOKIE_EXPIRY_DAYS
         )
         loginMachineSend({
            type: loginMachineEvents.requestUsernameOtp,
            username: username
         })
      }
   }

   const renderLeftIcon = (): ReactElement => (
      <div className='mr-8px'>
         <MailIcon />
      </div>
   )

   const renderHeader = (): ReactElement => (
      <>
         <span
            className={cn(
               Styles.loginTextStyles,
               loginCardTheme.loginTextColor
            )}
         >
            {customT('loginText')}
         </span>
         <span
            className={cn(
               Styles.loginDescriptionStyles,
               loginCardTheme.loginDescriptionTextColor
            )}
         >
            {customT('loginDescription')}
         </span>
      </>
   )

   const renderAppLogo = (): ReactElement => (
      <div className={'mb-[32px] mx-auto'}>
         <img
            style={{ width: `${clientConfig.login.logoWidth}px` }}
            alt='App Logo'
            src={clientConfig.login.logoURL}
         />
      </div>
   )

   return (
      <form
         className={cn(Styles.loginContainerStyles)}
         onSubmit={onClickSendOTP}
      >
         {isDefaultLoginLayout ? renderHeader() : renderAppLogo()}

         <TextField
            size='Small'
            onBlur={onBlurUsername}
            errorMessage={errorMessage || context.errorMessage}
            value={username || ''}
            type='text'
            onChange={onChangeUsername}
            label={customT('usernameLabel')}
            placeholder={customT('usernamePlaceholder')}
            leftElement={renderLeftIcon}
            errorTransitionClassName={errorTransitionClassName}
            autoFocus
            autoComplete='username'
            name='username'
         />

         <Button
            type='submit'
            hierarchy={Hierarchy.Primary}
            subVariant={SubVariant.Primary}
            size={'Large'}
            isDisabled={!isValid}
            rightIcon={() => <ArrowNarrowIcon />}
            className='mt-6'
            isLoading={loginMachineState.hasTag(
               loginMachineTags.requestOtpLoading
            )}
         >
            {customT('getOtp')}
         </Button>
      </form>
   )
}

export default observer(LoginWithUsername)
